import * as React from "react";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { Modal, Button, Form, Header } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { Link, TaskModel } from "@teamprevent/common/task/taskActions/models";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";

import { AddTaskForm } from "../../../tasks/components/addTaskForm";
import { ACTION_TASK } from "../../../crud/ohCase/actiontypes";
import { actionTask } from "../../../crud/ohCase/actions";
import { ApplicationState } from "../../../applicationState";
import { AddTaskFormModel, TaskType, AddFollowUpTaskModel, mapToCreateTaskModel } from "../../model";
import { taskSelector } from "../../selectors";
import { ImmunisationType } from "../../../crud/immunisationType";
import { ImmunisationTypeTask } from "../../../crud/immunisationTypeTask";
import { AdminUserListModelWithTeams, loadAdminUsersWithTeams } from "../../../crud/adminUserWithTeams";
import { adminUsersWithTeamsSelector } from "../../../crud/adminUserWithTeams/selectors";
import { getUserSortableName } from "../../../crud/adminUser/model";
import { mapAddTaskModelToCreateTaskDto } from "../../../crud/ohCase/ohCaseApiMappers";

interface StateProps {
    task: TaskModel;
    adminUsersWithTeams: AdminUserListModelWithTeams[];
    followUpTaskTypes: TaskType[];
    immunisationTypes?: ImmunisationType[];
    immunisationTypeTasks?: ImmunisationTypeTask[];
}

interface DispatchProps {
    close: () => void;
    submit: (oldTask: TaskModel, notes: string, followUpTask: AddTaskFormModel & { immunisationTypeTasks: Link[] }) => void;
    loadAdminUsers: () => void;
}

type Props = StateProps & DispatchProps;

interface OwnState {
    notes: string;
    followUpTask: AddFollowUpTaskModel;
    submitted: boolean;
}

class CloseTaskAndCreateFollowUpTaskModalUnconnected extends React.Component<Props, OwnState> {
    public state: OwnState = {
        notes: "",
        followUpTask: {
            task: {
                taskCode: null,
                taskType: null,
                dueDate: null,
                notes: null,
                immunisationTypeTasks: null
            },
            isValid: false
        },
        submitted: false
    };

    public componentDidMount() {
        if (!this.props.adminUsersWithTeams?.length) {
            this.props.loadAdminUsers();
        }
    }

    private getAdminUserOptions() {
        if (!this.props.adminUsersWithTeams?.length) {
            return [];
        }

        const adminUsersWithTeams = [...this.props.adminUsersWithTeams.sort((a, b) => getUserSortableName(a).localeCompare(getUserSortableName(b)))];
        adminUsersWithTeams.splice(0, 0, { id: "Unassigned", emailAddress: "Unassigned" });
        return adminUsersWithTeams;
    }

    public render() {
        const { task, followUpTaskTypes, close, immunisationTypes, immunisationTypeTasks } = this.props;
        const { followUpTask: newTask, submitted } = this.state;
        return (
            <Modal open>
                <Modal.Header content={task && task.description} />
                <Modal.Content>
                    <Form loading={!task}>
                        <Input.Textarea readOnly label="Previous Notes" value={this.props.task?.notes} />
                        <TextSnippetSelector onSelect={this.onNotesSnippetChange} />
                        <Input.Textarea
                            label="Current Notes"
                            required
                            showErrors={submitted}
                            onChange={this.onChangeNotes}
                            value={this.state.notes}
                        />
                        <Header as="h3" content="Follow-up Task" />
                        <AddTaskForm
                            defaultDueDate
                            ohCaseId={task && task.caseId}
                            task={newTask.task}
                            showErrors={submitted}
                            taskTypes={followUpTaskTypes}
                            immunisationTypes={immunisationTypes}
                            immunisationTypeTasks={immunisationTypeTasks}
                            onChange={this.onChangeFollowUpTask}
                            notesLabel="Follow-up Task Notes"
                            hideAssignmentOption={false}
                            assignmentOptionRequired={true}
                            adminUsers={this.getAdminUserOptions()}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <IsLoadingButton requestNames={ACTION_TASK} content="Action Task" onClick={this.submit} />
                    <Button content="Cancel" onClick={close} />
                </Modal.Actions>
            </Modal>
        );
    }

    private onChangeNotes = (notes: string) => {
        this.setState({ notes });
    };

    private onNotesSnippetChange = (snippet: string) => {
        const newNotes = (this.state.notes ? this.state.notes + " " : "") + snippet;
        this.setState({ notes: newNotes });
    };

    private onChangeFollowUpTask = (followUpTask: AddTaskFormModel, isValid: boolean) => {
        const immunisationTypeTaskIds = followUpTask.immunisationTypeTaskIds;

        let immunisationTypeTasks: Link[] = null;
        if (immunisationTypeTaskIds?.length > 0) {
            immunisationTypeTasks = immunisationTypeTaskIds.map(ittId => {
                const immunisationTypeTask = this.props.immunisationTypeTasks.find(itt => itt.id === ittId);
                return {
                    id: ittId,
                    name: immunisationTypeTask?.name
                };
            });
        }

        this.setState({
            ...this.state,
            followUpTask: {
                task: {
                    ...followUpTask,
                    immunisationTypeTasks
                },
                isValid
            }
        });
    };

    private submit = () => {
        this.setState({ submitted: true });

        const { notes, followUpTask } = this.state;

        if (notes && followUpTask && followUpTask.isValid) {
            this.props.submit(this.props.task, notes, followUpTask.task);
        }
    };
}

const mapStateToProps = (state: ApplicationState): StateProps => {
    const task = taskSelector(state);
    return {
        task,
        adminUsersWithTeams: adminUsersWithTeamsSelector(state),
        followUpTaskTypes: state.tasks.taskTypes,
        immunisationTypes: state.immunisationTypes,
        immunisationTypeTasks: state.immunisationTypeTasks
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, void, Action>): DispatchProps => ({
    close: () => dispatch(navigateToParentRoute()),
    submit: async (oldTask: TaskModel, notes: string, followUpTask: AddTaskFormModel & { immunisationTypeTasks: Link[] }) => {
        await dispatch(actionTask(
            oldTask.customer.id,
            oldTask.caseId,
            oldTask.id,
            notes,
            {
                ...mapAddTaskModelToCreateTaskDto(mapToCreateTaskModel(followUpTask, oldTask.caseId)),
                immunisationTypeTasks: followUpTask.immunisationTypeTasks,
            })
        );
        dispatch(navigateToParentRoute());
    },
    loadAdminUsers: () => dispatch(loadAdminUsersWithTeams())
});

export const CloseTaskAndCreateFollowUpTaskModal = connect(mapStateToProps, mapDispatchToProps)(CloseTaskAndCreateFollowUpTaskModalUnconnected);
